import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import api from "../lib/api";
import { usePageMeta } from "../lib/seo";
import { SiteHeader, SiteFooter } from "../components/SiteChrome";
import { fmtDate } from "../lib/time";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
export const imgSrc = (url) => (url && url.startsWith("/api/") ? `${API_BASE}${url}` : url);

export function PostCard({ p, big = false }) {
  return (
    <Link to={`/insights/${p.slug}`} className={`group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-transform hover:-translate-y-1 hover:border-indigo-200 ${big ? "md:col-span-2 md:flex-row" : ""}`} data-testid={`post-card-${p.slug}`}>
      <div className={`overflow-hidden bg-indigo-50 ${big ? "md:w-1/2" : ""}`}>
        {p.cover_url ? <img src={imgSrc(p.cover_url)} alt={p.title} loading="lazy" className="aspect-[16/9] h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          : <div className="flex aspect-[16/9] items-center justify-center font-head text-2xl font-bold text-indigo-200">Servantage Insights</div>}
      </div>
      <div className={`flex flex-1 flex-col p-5 sm:p-6 ${big ? "md:justify-center md:p-8" : ""}`}>
        <span className="text-xs font-bold uppercase tracking-[0.16em] text-indigo-600">{p.category}</span>
        <h2 className={`mt-2 font-head font-bold leading-snug text-slate-900 ${big ? "text-2xl sm:text-3xl" : "text-lg"}`}>{p.title}</h2>
        <p className="mt-2 line-clamp-3 text-sm text-slate-600">{p.summary}</p>
        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-slate-400">
          <span>{fmtDate(p.published_at)} · <Clock className="mb-0.5 inline h-3 w-3" /> {p.reading_minutes} min read</span>
          <ArrowRight className="h-4 w-4 text-indigo-500 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
}

export default function Insights() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("category") || "";
  const [d, setD] = useState(null);
  usePageMeta("Insights: Service Excellence, Customer Experience & Frontline Performance | Servantage", "/insights", false, {
    description: "Practical articles on frontline service excellence, customer feedback, closed-loop service recovery and employee performance for retail and restaurant teams.",
  });
  useEffect(() => {
    api.get(`/public/insights${cat ? `?category=${encodeURIComponent(cat)}` : ""}`).then((r) => setD(r.data)).catch(() => setD({ posts: [], categories: [] }));
  }, [cat]);

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-5 py-12 sm:px-6 sm:py-16">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">Servantage Insights</span>
        <h1 className="mt-3 max-w-3xl font-head text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">Ideas for teams who take service seriously.</h1>
        <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">Practical guides on frontline service excellence, customer feedback, service recovery and employee performance.</p>
        <div className="mt-8 flex flex-wrap gap-2" data-testid="insights-categories">
          {["", ...(d?.categories || [])].map((c) => (
            <button key={c || "all"} onClick={() => setParams(c ? { category: c } : {})} data-testid={`insights-cat-${c ? c.replace(/\W+/g, "-").toLowerCase() : "all"}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${cat === c ? "bg-[#4338CA] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{c || "All"}</button>
          ))}
        </div>
        {!d ? <p className="py-20 text-center text-slate-400">Loading articles…</p>
          : d.posts.length === 0 ? <p className="py-20 text-center text-slate-400" data-testid="insights-empty">New articles are on the way. Check back soon.</p>
          : (
            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="insights-list">
              {d.posts.map((p, i) => <PostCard key={p.id} p={p} big={i === 0 && !cat} />)}
            </div>
          )}
      </main>
      <SiteFooter />
    </div>
  );
}
